import * as React from "react";
import {
  Message,
  LineCreator,
  LineRating,
  EngagementConfiguration,
  MessageStatusType,
  LineType,
  ActionCardData,
  QnaResult
} from "../types";
import ImagePreview from "./ImagePreview";
import MessageTimestamp from "./MessageTimestamp";
import MessageControls from "./MessageControls";
import ConfigContext from "../context/ConfigContext";
import ImageContainer from "./ImageContainer";
import ActionCard from "./ActionCard";
import HtmlBlock from "./HtmlBlock";
import QnaResultView from "./QnaResultView";

const linkify = require("linkifyjs/html");

interface ConfigExtension {
  config: EngagementConfiguration;
}

interface Props {
  message: Message;
  skipTimestamp: boolean;
  onHeightChange(): void;
  onClickExit(): void;
  onSetLineRating(lineId: number, rating: number): void;
  onResend(): void;
  onClickActionCard: (name: string, data: string) => void;
}

interface State {
  isHovering: boolean;
  rating: LineRating;
}

class ChatMessage extends React.Component<Props & ConfigExtension, State> {
  state = {
    isHovering: false,
    rating: this.props.message.rating
  };

  textEl: HTMLSpanElement;

  componentDidMount() {
    this.hookUpExitChatLink();
  }

  hookUpExitChatLink() {
    if (!this.textEl) {
      return;
    }

    const link = this.textEl.querySelector("#velaro-exit-chat-lnk");

    if (!link) {
      return;
    }

    if (link.getAttribute("listener")) {
      return;
    }

    link.addEventListener("click", this.props.onClickExit);
  }

  getLineTypeClassName() {
    switch (this.props.message.lineCreator) {
      case LineCreator.Agent:
        return "agent";
      case LineCreator.Bot:
        return "bot";
      case LineCreator.Notice:
        return "notice";
      case LineCreator.System:
        return "system";
      case LineCreator.Visitor:
        return "visitor";
    }
  }

  onMouseEnter() {
    this.setState({
      isHovering: true
    });
  }

  onMouseLeave() {
    this.setState({
      isHovering: false
    });
  }

  onChangeRating(rating: LineRating) {
    this.setState({
      rating: rating
    });

    this.props.onSetLineRating(this.props.message.id, rating);
  }

  renderHeadshot() {
    const creator = this.props.message.lineCreator;

    if (creator !== LineCreator.Agent && creator !== LineCreator.Bot) {
      return null;
    }

    return (
      <div className="velaro-message-headshot">
        <span className="headshot">
          <img
            src={this.getChatMessageImageUrl()}
            alt={`${this.props.message.author} profile`}
          />
        </span>
      </div>
    );
  }

  getChatMessageImageUrl() {
    if (this.props.config.ChatLineImageType === "AgentHeadshot") {
      return this.props.message.headshotUrl;
    }

    return this.props.config.HeaderLogoUrl;
  }

  renderImagePreview() {
    const creator = this.props.message.lineCreator;
    if (
      creator !== LineCreator.Agent &&
      creator !== LineCreator.Bot &&
      creator !== LineCreator.Visitor
    ) {
      return null;
    }

    return <ImagePreview text={this.props.message.text} />;
  }

  renderTimestamp() {
    if (this.props.skipTimestamp) {
      return null;
    }
    return <MessageTimestamp timestamp={this.props.message.timestamp} />;
  }

  renderMessageControls() {
    if (!this.props.config.ChatLineRatingEnabled) {
      return null;
    }

    if (!this.state.isHovering) {
      return null;
    }

    if (this.props.message.lineCreator !== LineCreator.Agent) {
      return null;
    }

    return (
      <MessageControls
        onChangeRating={(rating: LineRating) => this.onChangeRating(rating)}
        rating={this.state.rating}
      />
    );
  }

  renderMessage() {
    if (this.props.message.status === MessageStatusType.Failure) {
      return this.renderUnsentMessage();
    } else if (this.props.message.status === MessageStatusType.Resending) {
      return this.renderResendingMessage();
    } else {
      return this.renderNormalMessage();
    }
  }

  tryLinkify(text: string) {
    try {
      return linkify(text);
    } catch {
      console.warn("linkify failed for text", text);
      return text;
    }
  }

  renderTextSpan() {
    return (
      <ImageContainer onImageLoad={() => this.props.onHeightChange()}>
        <span ref={(el) => (this.textEl = el)} className="text">
          <span
            dangerouslySetInnerHTML={{
              __html: this.tryLinkify(this.props.message.text)
            }}
          />
          {this.renderImagePreview()}
        </span>
      </ImageContainer>
    );
  }

  renderActionCard() {
    const actionCardData = this.props.message.metadata
      .ActionCard as ActionCardData;

    return (
      <ActionCard
        data={actionCardData}
        onClick={this.props.onClickActionCard}
      />
    );
  }

  renderHtmlBlock() {
    const html = this.props.message.metadata.Html as string;
    return <HtmlBlock html={html} />;
  }

  renderQnaCarousel() {
    const qnaResult = this.props.message.metadata.QnaResult as QnaResult;
    return <QnaResultView data={qnaResult} />;
  }

  renderMessageBody() {
    switch (this.props.message.type) {
      case LineType.ActionCard:
        return this.renderActionCard();
      case LineType.HTML:
        return this.renderHtmlBlock();
      case LineType.QnaResults:
        return this.renderQnaCarousel();
      default:
        return this.renderTextSpan();
    }
  }

  renderUnsentMessage() {
    return (
      <div
        className={`velaro-message velaro-message-unsent ${this.getLineTypeClassName()}`}
        tabIndex={0}
        role="button"
        onClick={() => {
          this.props.onResend();
        }}
        onKeyDown={(e) => {
          return e.keyCode !== 13 || this.props.onResend();
        }}
      >
        {this.renderHeadshot()}
        <div className="velaro-message-body">
          {this.renderTextSpan()}
          <div className="velaro-message-unsent-description">
            Failed to send message. Click to retry.
          </div>
        </div>
      </div>
    );
  }

  renderResendingMessage() {
    return (
      <div
        className={`velaro-message velaro-message-resending ${this.getLineTypeClassName()}`}
      >
        {this.renderHeadshot()}
        <div className="velaro-message-body">
          {this.renderTextSpan()}
          <div className="velaro-message-resending-description">Sending...</div>
        </div>
      </div>
    );
  }

  renderNormalMessage() {
    return (
      <div
        className={`velaro-message ${this.getLineTypeClassName()}`}
        tabIndex={-1}
        role="listitem"
      >
        {this.renderHeadshot()}
        <div
          className="velaro-message-body"
          onMouseEnter={() => this.onMouseEnter()}
          onMouseLeave={() => this.onMouseLeave()}
        >
          {this.renderMessageControls()}
          {this.renderMessageBody()}
        </div>
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderTimestamp()}
        {this.renderMessage()}
      </React.Fragment>
    );
  }
}

export default (props: Props) => (
  <ConfigContext.Consumer>
    {(config) => <ChatMessage config={config} {...props} />}
  </ConfigContext.Consumer>
);
